import { useState, useEffect } from 'react';
import { CheckCircle, Clock, User, Calendar, DollarSign, PenTool, Layout, FileText, Wrench, XCircle, AlertCircle, Star, ThumbsUp, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { ServiceTicket, serviceTicketService, autoRateTicket, submitTicketReview } from '../../utils/services/serviceTicketService';
import { auth } from '../../utils/firebase/client';
import { auditInvoice } from '../../utils/analytics/marketBenchmarks';
import { learnFromTicket, analyzePriceLikelihood, PricingAnalysis } from '../../utils/analytics/pricingEngine';
import { cn } from '../ui/utils';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface TicketDetailsSheetProps {
    ticket: ServiceTicket | null;
    open: boolean;
    onClose: () => void;
    onUpdate: () => void;
    users?: any[]; // For assignment
    isDarkMode: boolean;
}

export function TicketDetailsSheet({ ticket, open, onClose, onUpdate, users, isDarkMode }: TicketDetailsSheetProps) {
    const [activeTab, setActiveTab] = useState('details');
    const [isLoading, setIsLoading] = useState(false);

    // Editable Fields State
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState<ServiceTicket['status']>('open');
    const [assignedTo, setAssignedTo] = useState('');

    // Resolution & Review State
    const [showResolutionDialog, setShowResolutionDialog] = useState(false);
    const [finalCost, setFinalCost] = useState('');

    // Invoice Audit State
    const [billingType, setBillingType] = useState<'fixed' | 'hourly'>('fixed');
    const [hoursBilled, setHoursBilled] = useState('');
    const [hourlyRate, setHourlyRate] = useState('');
    const [liveAnalysis, setLiveAnalysis] = useState<PricingAnalysis | null>(null);

    const [ratingScore, setRatingScore] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [isReviewExpanded, setIsReviewExpanded] = useState(false);

    useEffect(() => {
        if (ticket) {
            setNotes(ticket.notes || '');
            setStatus(ticket.status);
            setAssignedTo(ticket.assignedTo || '');
            // Pre-fill cost if exists
            setFinalCost(ticket.actualCost?.toString() || ticket.estimatedCost?.toString() || '');
            // Reset modal state
            setRatingScore(5);
            setReviewComment('');
            setIsReviewExpanded(false);
            setBillingType('fixed');
            setHoursBilled('');
            setHourlyRate('');
            setLiveAnalysis(null);
        }
    }, [ticket]);

    // 🧠 Active Intelligence: Analyze price against Live Market Index
    useEffect(() => {
        if (!ticket || !finalCost) return;

        const checkPrice = async () => {
            const value = billingType === 'hourly' ? parseFloat(hourlyRate) : parseFloat(finalCost);
            if (!value || isNaN(value)) return;

            const analysis = await analyzePriceLikelihood(ticket.type, billingType, value);
            setLiveAnalysis(analysis);
        };

        // Debounce to prevent slamming DB while typing
        const timer = setTimeout(checkPrice, 800);
        return () => clearTimeout(timer);
    }, [finalCost, hourlyRate, billingType, ticket?.type]);

    if (!ticket) return null;

    const handleStatusChange = async (newStatus: string) => {
        try {
            setIsLoading(true);
            await serviceTicketService.updateServiceTicket(ticket.id, { status: newStatus as any });
            setStatus(newStatus as any);
            toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
            onUpdate();
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAssignmentChange = async (userId: string) => {
        try {
            setIsLoading(true);
            const user = users?.find(u => u.id === userId);
            await serviceTicketService.updateServiceTicket(ticket.id, {
                assignedTo: userId,
                assignedToName: user ? (user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email) : 'Unknown'
            });
            setAssignedTo(userId);
            toast.success('Assignment updated');
            onUpdate();
        } catch (error) {
            toast.error('Failed to update assignment');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveNotes = async () => {
        try {
            setIsLoading(true);
            await serviceTicketService.updateServiceTicket(ticket.id, { notes });
            toast.success('Notes saved');
            onUpdate();
        } catch (error) {
            toast.error('Failed to save notes');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseTicket = () => {
        setShowResolutionDialog(true);
    };

    const submitResolution = async () => {
        try {
            setIsLoading(true);

            // 1. Update Cost & Status
            const cost = parseFloat(finalCost);
            const hours = parseFloat(hoursBilled);
            const rate = parseFloat(hourlyRate);

            await serviceTicketService.updateServiceTicket(ticket.id, {
                status: 'closed',
                actualCost: isNaN(cost) ? undefined : cost,
                billingDetails: billingType === 'hourly' ? {
                    hours: isNaN(hours) ? 0 : hours,
                    rate: isNaN(rate) ? 0 : rate,
                    type: 'hourly'
                } : { type: 'fixed' },
                completedAt: ticket.completedAt || new Date()
            });

            // 🧠 TRAIN THE MODEL (Fire & Forget)
            // This is the "Google Level" complexity made invisible.
            // Every closed ticket updates the distributed ledger of costs.
            learnFromTicket(
                ticket.type,
                billingType,
                cost,
                billingType === 'hourly' ? rate : undefined
            ).catch(err => console.error("Learning background task failed", err));

            // 2. Handle Rating
            if (ratingScore === 5 && !reviewComment && !isReviewExpanded) {
                // Happy Path: Auto Rate
                await autoRateTicket(ticket.id);
                toast.success('Ticket closed & verified (Standard 5★)');
            } else {
                // Manual Review
                await submitTicketReview(ticket.id, {
                    score: ratingScore,
                    comment: reviewComment,
                    tags: ratingScore < 3 ? ['Requires Attention'] : ['Manual Review']
                }, auth.currentUser?.uid || 'unknown');
                toast.success('Ticket closed with review');
            }

            setShowResolutionDialog(false);
            onClose(); // Close the sheet
            onUpdate();
        } catch (error) {
            console.error(error);
            toast.error('Failed to resolve ticket');
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'open': return Clock;
            case 'in_progress': return Wrench;
            case 'resolved': return CheckCircle;
            case 'cancelled': return XCircle;
            default: return AlertCircle;
        }
    };

    const StatusIcon = getStatusIcon(status);

    return (
        <Sheet open={open} onOpenChange={onClose}>
            <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col h-full bg-white dark:bg-slate-950">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                    <div className="flex items-start justify-between mb-4">
                        <Badge variant="outline" className={cn(
                            "uppercase tracking-wider font-mono",
                            ticket.priority === 'critical' ? "text-red-600 border-red-200 bg-red-50" :
                                ticket.priority === 'high' ? "text-orange-600 border-orange-200 bg-orange-50" :
                                    "text-blue-600 border-blue-200 bg-blue-50"
                        )}>
                            {ticket.priority} Priority
                        </Badge>
                        <Badge variant="secondary" className="flex items-center gap-1.5 capitalize">
                            <StatusIcon className="w-3.5 h-3.5" />
                            {status.replace('_', ' ')}
                        </Badge>
                    </div>

                    <SheetTitle className="text-xl font-bold mb-2 pr-6 leading-tight">
                        {ticket.title}
                    </SheetTitle>
                    <SheetDescription className="flex items-center gap-4 text-xs">
                        <span className="font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-slate-600 dark:text-slate-400">
                            #{ticket.ticketNumber}
                        </span>
                        <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(ticket.createdAt).toLocaleDateString()}
                        </span>
                    </SheetDescription>
                </div>

                {/* Tabs & Content */}
                <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
                    <div className="px-6 pt-2 border-b border-slate-100 dark:border-slate-800">
                        <TabsList className="bg-transparent p-0 w-full justify-start h-auto">
                            <TabsTrigger
                                value="details"
                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none px-4 py-3 text-sm"
                            >
                                Details
                            </TabsTrigger>
                            <TabsTrigger
                                value="activity"
                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none px-4 py-3 text-sm"
                            >
                                Activity Log
                            </TabsTrigger>
                            <TabsTrigger
                                value="parts"
                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none px-4 py-3 text-sm"
                            >
                                Parts Used
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <ScrollArea className="flex-1 bg-slate-50/30 dark:bg-slate-900/10">
                        <div className="p-6 space-y-8">
                            <TabsContent value="details" className="mt-0 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">

                                {/* Info Cards Grid */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                                        <div className="text-xs font-medium text-slate-500 mb-1">Well Location</div>
                                        <div className="font-semibold text-sm flex items-center gap-2">
                                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg">
                                                <Layout className="w-4 h-4" />
                                            </div>
                                            {ticket.wellName}
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                                        <div className="text-xs font-medium text-slate-500 mb-1">Estimated Cost</div>
                                        <div className="font-semibold text-sm flex items-center gap-2">
                                            <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-lg">
                                                <DollarSign className="w-4 h-4" />
                                            </div>
                                            {ticket.estimatedCost ? `$${ticket.estimatedCost.toLocaleString()}` : 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="space-y-3">
                                    <h3 className="text-sm font-semibold flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-slate-500" />
                                        Description
                                    </h3>
                                    <div className="p-4 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                                        {ticket.description}
                                    </div>
                                </div>

                                {/* Assignment & Status */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <h3 className="text-sm font-semibold flex items-center gap-2">
                                            <User className="w-4 h-4 text-slate-500" />
                                            Assignee
                                        </h3>
                                        {users ? (
                                            <Select value={assignedTo} onValueChange={handleAssignmentChange} disabled={isLoading}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Unassigned" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                                    {users.map(u => {
                                                        const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                                                        return (
                                                            <SelectItem key={u.id} value={u.id}>
                                                                <div className="flex items-center gap-2">
                                                                    <Avatar className="w-5 h-5">
                                                                        <AvatarFallback className="text-[10px]">{name?.[0]?.toUpperCase()}</AvatarFallback>
                                                                    </Avatar>
                                                                    {name}
                                                                </div>
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <div className="p-2 border rounded text-sm text-muted-foreground">{ticket.assignedToName || 'Unassigned'}</div>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        <h3 className="text-sm font-semibold flex items-center gap-2">
                                            <Wrench className="w-4 h-4 text-slate-500" />
                                            Current Status
                                        </h3>
                                        <Select value={status} onValueChange={handleStatusChange} disabled={isLoading}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="open">Open</SelectItem>
                                                <SelectItem value="assigned">Assigned</SelectItem>
                                                <SelectItem value="in_progress">In Progress</SelectItem>
                                                <SelectItem value="waiting_parts">Waiting on Parts</SelectItem>
                                                <SelectItem value="resolved">Resolved</SelectItem>
                                                <SelectItem value="closed">Closed</SelectItem>
                                                <SelectItem value="cancelled">Cancelled</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <Separator />

                                {/* Notes Section */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-semibold flex items-center gap-2">
                                            <PenTool className="w-4 h-4 text-slate-500" />
                                            Notes & Resolution
                                        </h3>
                                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={handleSaveNotes} disabled={isLoading}>
                                            Save Notes
                                        </Button>
                                    </div>
                                    <Textarea
                                        placeholder="Add work notes, technician updates, or resolution details..."
                                        className="resize-none min-h-[120px] bg-white dark:bg-slate-900"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>

                            </TabsContent>

                            <TabsContent value="activity">
                                <div className="text-center py-8 text-muted-foreground text-sm">
                                    Activity log coming soon...
                                </div>
                            </TabsContent>

                            <TabsContent value="parts">
                                <div className="text-center py-8 text-muted-foreground text-sm">
                                    Parts inventory integration coming soon...
                                </div>
                            </TabsContent>
                        </div>
                    </ScrollArea>
                </Tabs>

                {/* Footer */}
                <SheetFooter className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex sm:justify-between items-center w-full">
                    <Button variant="outline" onClick={onClose}>Close</Button>
                    <div className="flex gap-2">
                        {status !== 'resolved' && (
                            <Button
                                onClick={() => handleStatusChange('resolved')}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                disabled={isLoading}
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark Resolved
                            </Button>
                        )}
                        {status === 'resolved' && (
                            <Button
                                onClick={handleCloseTicket}
                                className="bg-slate-900 hover:bg-black text-white px-6 shadow-lg shadow-slate-900/20"
                                disabled={isLoading}
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Complete & Verify
                            </Button>
                        )}
                    </div>
                </SheetFooter>

                {/* Smart Resolution Dialog */}
                <Dialog open={showResolutionDialog} onOpenChange={setShowResolutionDialog}>
                    <DialogContent className="sm:max-w-md bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                        <DialogHeader>
                            <DialogTitle>Job Completion</DialogTitle>
                            <DialogDescription>Verify the work and performance to close this ticket.</DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6 py-4">
                            {/* Cost & Invoice Verification */}
                            <div className="space-y-4">
                                <Label>Billing Confirmation</Label>

                                {/* Toggle Billing Type */}
                                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => setBillingType('fixed')}
                                        className={cn(
                                            "py-1.5 text-xs font-medium rounded-md transition-all",
                                            billingType === 'fixed'
                                                ? "bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white"
                                                : "text-slate-500 hover:text-slate-900 dark:text-slate-400"
                                        )}
                                    >
                                        Flat / Unit Price
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setBillingType('hourly');
                                            // Reset cost to calc mode
                                            if (finalCost && !hoursBilled) setFinalCost('');
                                        }}
                                        className={cn(
                                            "py-1.5 text-xs font-medium rounded-md transition-all",
                                            billingType === 'hourly'
                                                ? "bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white"
                                                : "text-slate-500 hover:text-slate-900 dark:text-slate-400"
                                        )}
                                    >
                                        Hourly Labor
                                    </button>
                                </div>

                                {billingType === 'fixed' ? (
                                    <div className="relative animate-in fade-in zoom-in-95 duration-200">
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <Input
                                                type="number"
                                                placeholder="Total Invoice Amount"
                                                className="pl-9"
                                                value={finalCost}
                                                onChange={(e) => setFinalCost(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3 animate-in fade-in zoom-in-95 duration-200">
                                        <div className="space-y-1">
                                            <Label className="text-[10px] text-slate-500">Hours Billed</Label>
                                            <div className="relative">
                                                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                                <Input
                                                    type="number"
                                                    placeholder="0.0"
                                                    className="pl-8 h-9 text-sm"
                                                    value={hoursBilled}
                                                    onChange={(e) => {
                                                        const h = e.target.value;
                                                        setHoursBilled(h);
                                                        // Auto-calc total
                                                        if (h && hourlyRate) setFinalCost((parseFloat(h) * parseFloat(hourlyRate)).toFixed(2));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-[10px] text-slate-500">Hourly Rate ($)</Label>
                                            <div className="relative">
                                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                                <Input
                                                    type="number"
                                                    placeholder="0.00"
                                                    className="pl-8 h-9 text-sm"
                                                    value={hourlyRate}
                                                    onChange={(e) => {
                                                        const r = e.target.value;
                                                        setHourlyRate(r);
                                                        // Auto-calc total
                                                        if (hoursBilled && r) setFinalCost((parseFloat(hoursBilled) * parseFloat(r)).toFixed(2));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-2 text-right text-xs font-bold text-slate-700 dark:text-slate-300 flex justify-end gap-3 items-center">
                                            {/* AI Signal Badge */}
                                            {liveAnalysis && (
                                                <Badge variant="outline" className={cn(
                                                    "h-5 text-[10px] px-1.5 gap-1",
                                                    liveAnalysis.status === 'good' ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                                                        liveAnalysis.status === 'fair' ? "border-blue-200 bg-blue-50 text-blue-700" :
                                                            liveAnalysis.status === 'high' ? "border-amber-200 bg-amber-50 text-amber-700" :
                                                                "border-rose-200 bg-rose-50 text-rose-700 animate-pulse"
                                                )}>
                                                    <span className="relative flex h-1.5 w-1.5">
                                                        {liveAnalysis.status === 'anomaly' && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>}
                                                        <span className={cn("relative inline-flex rounded-full h-1.5 w-1.5",
                                                            liveAnalysis.status === 'good' ? "bg-emerald-500" :
                                                                liveAnalysis.status === 'fair' ? "bg-blue-500" :
                                                                    liveAnalysis.status === 'high' ? "bg-amber-500" : "bg-rose-500"
                                                        )}></span>
                                                    </span>
                                                    {liveAnalysis.status === 'anomaly' ? `sigma alert (+${liveAnalysis.zScore.toFixed(1)}σ)` :
                                                        liveAnalysis.status === 'good' ? 'market leader' :
                                                            `market ${liveAnalysis.percentile}th %`}
                                                </Badge>
                                            )}
                                            <span>Total: ${finalCost || '0.00'}</span>
                                        </div>
                                    </div>
                                )}

                                {/* SMART AUDIT ENGINE */}
                                {ticket.estimatedCost && finalCost && (
                                    <div className="space-y-2 mt-2">
                                        {/* 1. Internal Budget Audit */}
                                        {parseFloat(finalCost) > ticket.estimatedCost * 1.2 && (
                                            <Alert variant="destructive" className="py-2 bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30">
                                                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                                <AlertTitle className="text-xs font-bold text-red-800 dark:text-red-300">Budget Overrun</AlertTitle>
                                                <AlertDescription className="text-xs text-red-700 dark:text-red-400">
                                                    {Math.round(((parseFloat(finalCost) - ticket.estimatedCost) / ticket.estimatedCost) * 100)}% over estimate.
                                                </AlertDescription>
                                            </Alert>
                                        )}

                                        {/* 2. External Market Audit */}
                                        {(() => {
                                            const audit = auditInvoice(
                                                ticket.type,
                                                billingType,
                                                parseFloat(finalCost),
                                                parseFloat(hoursBilled),
                                                parseFloat(hourlyRate)
                                            );

                                            if (audit.status !== 'good') {
                                                const isCritical = audit.status === 'critical';
                                                return (
                                                    <Alert className={cn(
                                                        "py-2 border",
                                                        isCritical
                                                            ? "bg-rose-50 border-rose-200 dark:bg-rose-900/10 dark:border-rose-900/30"
                                                            : "bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-900/30"
                                                    )}>
                                                        {audit.type === 'rate'
                                                            ? <DollarSign className={cn("h-4 w-4", isCritical ? "text-rose-600" : "text-amber-600")} />
                                                            : <Clock className={cn("h-4 w-4", isCritical ? "text-rose-600" : "text-amber-600")} />
                                                        }
                                                        <AlertTitle className={cn("text-xs font-bold", isCritical ? "text-rose-800 dark:text-rose-300" : "text-amber-800 dark:text-amber-300")}>
                                                            {audit.type === 'rate' ? 'Rate Check' : audit.type === 'efficiency' ? 'Efficiency Check' : 'Market Check'}
                                                        </AlertTitle>
                                                        <AlertDescription className={cn("text-xs", isCritical ? "text-rose-700 dark:text-rose-400" : "text-amber-700 dark:text-amber-400")}>
                                                            {audit.message}
                                                            <div className="mt-0.5 opacity-80 font-medium">{audit.details}</div>
                                                        </AlertDescription>
                                                    </Alert>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                )}
                            </div>

                            <Separator />

                            {/* Performance Review */}
                            <div className="space-y-3">
                                <Label>Vendor Performance</Label>
                                <div className="flex gap-1 justify-center py-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => {
                                                setRatingScore(star);
                                                if (star < 5) setIsReviewExpanded(true);
                                            }}
                                            className="transition-transform hover:scale-110 focus:outline-none"
                                        >
                                            <Star
                                                className={cn(
                                                    "w-8 h-8 transition-colors",
                                                    star <= ratingScore ? "fill-amber-400 text-amber-400" : "text-slate-300"
                                                )}
                                            />
                                        </button>
                                    ))}
                                </div>
                                <div className="text-center text-xs font-medium text-slate-500">
                                    {ratingScore === 5 ? 'Excellent Service' : ratingScore === 4 ? 'Good' : ratingScore === 3 ? 'Average' : 'Subpar'}
                                </div>
                            </div>

                            {(ratingScore < 5 || isReviewExpanded) && (
                                <div className="space-y-2 animate-in slide-in-from-top-2">
                                    <Label>Feedback / Issues</Label>
                                    <Textarea
                                        placeholder="What went wrong? Or add specific praise..."
                                        value={reviewComment}
                                        onChange={(e) => setReviewComment(e.target.value)}
                                        className="text-sm"
                                    />
                                </div>
                            )}

                            {ratingScore === 5 && !isReviewExpanded && (
                                <div className="flex justify-center text-xs text-slate-400 italic">
                                    <ThumbsUp className="w-3 h-3 mr-1" />
                                    No comment needed for 5-star service.
                                    <button
                                        type="button"
                                        onClick={() => setIsReviewExpanded(true)}
                                        className="ml-2 text-blue-500 hover:underline flex items-center"
                                    >
                                        <MessageSquare className="w-3 h-3 mr-1" />
                                        Add Note
                                    </button>
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowResolutionDialog(false)}>Cancel</Button>
                            <Button
                                onClick={submitResolution}
                                className="bg-slate-900 hover:bg-black text-white"
                                disabled={isLoading}
                            >
                                {ratingScore === 5 && !reviewComment ? 'Quick Close (Auto-Rate)' : 'Submit Review & Close'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </SheetContent>
        </Sheet>
    );
}
